// Draw-the-Wave verb — scribble a single cycle on a canvas; turn it
// into a PeriodicWave; render to a sample via OfflineAudioContext with
// a pitch + ADSR so the output is musically usable.
//
// The user's drawing is stored as normalised points over one cycle. On
// render:
//   1. Compute the DFT of the drawn waveform to get real/imag coefficient
//      arrays.
//   2. setPeriodicWave on an Oscillator; modulate with a gain envelope;
//      render via OfflineAudioContext.
//
// Storage-wise, origin.pointsBase64 holds a compact Float32 encoding of
// the normalised drawn curve so the exact wave is reproducible.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { VerbProps } from '../types';

const POINTS = 256; // display resolution + also table length used for DFT
const CANVAS_HEIGHT = 160;

export function DrawVerb({ audioContext, onBuffer, onStatus }: VerbProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [wave, setWave] = useState<Float32Array>(() => {
    // Seed with a gentle sine so the canvas isn't empty on first visit.
    const w = new Float32Array(POINTS);
    for (let i = 0; i < POINTS; i++) w[i] = Math.sin((i / POINTS) * 2 * Math.PI) * 0.8;
    return w;
  });
  const [pitchMidi, setPitchMidi] = useState(60);
  const [lengthMs, setLengthMs] = useState(600);
  const [rendering, setRendering] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(0);

  useEffect(() => {
    const el = canvasRef.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setCanvasWidth(Math.round(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Draw on canvas whenever wave or width changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvasWidth < 1) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(canvasWidth * dpr));
    canvas.height = Math.max(1, Math.floor(CANVAS_HEIGHT * dpr));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#09090B';
    ctx.fillRect(0, 0, canvasWidth, CANVAS_HEIGHT);
    // Zero-crossing baseline.
    ctx.strokeStyle = '#27272A';
    ctx.beginPath();
    ctx.moveTo(0, CANVAS_HEIGHT / 2);
    ctx.lineTo(canvasWidth, CANVAS_HEIGHT / 2);
    ctx.stroke();
    // Drawn curve.
    ctx.strokeStyle = '#FDE047';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let x = 0; x < canvasWidth; x++) {
      const i = Math.floor((x / canvasWidth) * POINTS);
      const v = wave[i] ?? 0;
      const y = CANVAS_HEIGHT / 2 - v * (CANVAS_HEIGHT / 2 - 6);
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }, [wave, canvasWidth]);

  const drawingRef = useRef(false);

  const pixelToIndexValue = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>): { idx: number; v: number } | null => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (rect.width < 1) return null;
      const idx = Math.max(0, Math.min(POINTS - 1, Math.floor((x / rect.width) * POINTS)));
      const v = Math.max(-1, Math.min(1, (CANVAS_HEIGHT / 2 - y) / (CANVAS_HEIGHT / 2)));
      return { idx, v };
    },
    [],
  );

  const updatePoint = useCallback(
    (idx: number, v: number) => {
      setWave((prev) => {
        const next = new Float32Array(prev.length);
        next.set(prev);
        next[idx] = v;
        return next;
      });
    },
    [],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      drawingRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      const p = pixelToIndexValue(e);
      if (p) updatePoint(p.idx, p.v);
    },
    [pixelToIndexValue, updatePoint],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return;
      const p = pixelToIndexValue(e);
      if (p) updatePoint(p.idx, p.v);
    },
    [pixelToIndexValue, updatePoint],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      drawingRef.current = false;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // already released
      }
    },
    [],
  );

  const presets = useMemo(
    () => ({
      sine: () => setWave(makeSine()),
      saw: () => setWave(makeSaw()),
      square: () => setWave(makeSquare()),
      triangle: () => setWave(makeTriangle()),
      clear: () => setWave(new Float32Array(POINTS)),
    }),
    [],
  );

  const handleRender = useCallback(async () => {
    if (!audioContext) {
      onStatus('engine not booted', 'warn');
      return;
    }
    setRendering(true);
    try {
      const freq = 440 * Math.pow(2, (pitchMidi - 69) / 12);
      const frames = Math.max(1, Math.round((lengthMs / 1000) * audioContext.sampleRate));
      const rCtx = new OfflineAudioContext(2, frames, audioContext.sampleRate);
      const { real, imag } = waveToFourier(wave);
      const periodic = rCtx.createPeriodicWave(real, imag, {
        disableNormalization: false,
      });
      const osc = rCtx.createOscillator();
      osc.setPeriodicWave(periodic);
      osc.frequency.value = freq;
      const gain = rCtx.createGain();
      const t0 = rCtx.currentTime;
      const lenS = lengthMs / 1000;
      // Simple AR envelope — attack 10ms, release fills the rest.
      const attack = Math.min(0.02, lenS * 0.1);
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.8, t0 + attack);
      gain.gain.linearRampToValueAtTime(0, t0 + lenS);
      osc.connect(gain);
      gain.connect(rCtx.destination);
      osc.start(t0);
      osc.stop(t0 + lenS);
      const buffer = await rCtx.startRendering();
      const pointsBase64 = floatArrayToBase64(wave);
      onBuffer(buffer, {
        suggestedName: `draw_${pitchMidi}`,
        origin: { kind: 'wavetable', pointsBase64 },
        pitchedRootMidi: pitchMidi,
      });
      onStatus('Rendered wavetable sample — trim and save.', 'ok');
    } catch (e) {
      onStatus(
        'Render failed: ' + (e instanceof Error ? e.message : String(e)),
        'err',
      );
    } finally {
      setRendering(false);
    }
  }, [audioContext, lengthMs, onBuffer, onStatus, pitchMidi, wave]);

  return (
    <div className="flex flex-col gap-3 min-h-[200px]">
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-2 text-xs text-zinc-400">
        Scribble one cycle of a waveform. Press Render to bake it at the chosen pitch.
      </div>
      <div className="relative">
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: CANVAS_HEIGHT, touchAction: 'none' }}
          className="block bg-zinc-950 border border-zinc-800 cursor-crosshair"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <button
          type="button"
          onClick={presets.sine}
          className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
        >
          sine
        </button>
        <button
          type="button"
          onClick={presets.saw}
          className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
        >
          saw
        </button>
        <button
          type="button"
          onClick={presets.square}
          className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
        >
          square
        </button>
        <button
          type="button"
          onClick={presets.triangle}
          className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
        >
          triangle
        </button>
        <button
          type="button"
          onClick={presets.clear}
          className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-zinc-900 border border-zinc-800 text-zinc-500 hover:bg-zinc-800"
        >
          clear
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 text-xs">
        <label className="flex items-center gap-2">
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500 w-12 shrink-0">
            Pitch
          </span>
          <input
            type="range"
            min={24}
            max={96}
            step={1}
            value={pitchMidi}
            onChange={(e) => setPitchMidi(Number(e.target.value))}
            className="accent-yellow-300 flex-1 min-w-0"
          />
          <span className="tabular-nums w-16 text-right text-zinc-300 truncate">
            {pitchMidi}·{(440 * Math.pow(2, (pitchMidi - 69) / 12)).toFixed(0)}Hz
          </span>
        </label>
        <label className="flex items-center gap-2">
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500 w-12 shrink-0">
            Length
          </span>
          <input
            type="range"
            min={100}
            max={3000}
            step={50}
            value={lengthMs}
            onChange={(e) => setLengthMs(Number(e.target.value))}
            className="accent-yellow-300 flex-1 min-w-0"
          />
          <span className="tabular-nums w-12 text-right text-zinc-300">{lengthMs}ms</span>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleRender()}
          disabled={rendering}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
        >
          {rendering ? 'Rendering…' : '● Render'}
        </button>
      </div>
    </div>
  );
}

// ── DFT: wave → (real, imag) Fourier coefficients for PeriodicWave.
// PeriodicWave takes arrays where coefficients[0] is DC (ignored by the
// oscillator), coefficients[1] is the fundamental, etc. Array length
// cap chosen to balance harmonic content + construction cost.
function waveToFourier(wave: Float32Array): {
  real: Float32Array;
  imag: Float32Array;
} {
  const N = wave.length;
  const harmonics = Math.min(64, Math.floor(N / 2));
  const real = new Float32Array(harmonics + 1);
  const imag = new Float32Array(harmonics + 1);
  // k=0 is DC — leave zero so no accumulated bias.
  for (let k = 1; k <= harmonics; k++) {
    let a = 0;
    let b = 0;
    for (let n = 0; n < N; n++) {
      const phase = (2 * Math.PI * k * n) / N;
      a += wave[n]! * Math.cos(phase);
      b += wave[n]! * Math.sin(phase);
    }
    // Standard Fourier coefficient convention for PeriodicWave.
    real[k] = (2 * a) / N;
    imag[k] = (-2 * b) / N;
  }
  return { real, imag };
}

// ── Encoding helpers.

function floatArrayToBase64(arr: Float32Array): string {
  // Copy into a fresh ArrayBuffer so we know the byte view is non-shared.
  const copy = new Float32Array(arr.length);
  copy.set(arr);
  const bytes = new Uint8Array(copy.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

// ── Reference waveform presets.

function makeSine(): Float32Array {
  const w = new Float32Array(POINTS);
  for (let i = 0; i < POINTS; i++) w[i] = Math.sin((i / POINTS) * 2 * Math.PI) * 0.8;
  return w;
}
function makeSaw(): Float32Array {
  const w = new Float32Array(POINTS);
  for (let i = 0; i < POINTS; i++) w[i] = ((i / POINTS) * 2 - 1) * 0.8;
  return w;
}
function makeSquare(): Float32Array {
  const w = new Float32Array(POINTS);
  for (let i = 0; i < POINTS; i++) w[i] = i < POINTS / 2 ? 0.8 : -0.8;
  return w;
}
function makeTriangle(): Float32Array {
  const w = new Float32Array(POINTS);
  for (let i = 0; i < POINTS; i++) {
    const t = i / POINTS;
    w[i] = (t < 0.5 ? 4 * t - 1 : 3 - 4 * t) * 0.8;
  }
  return w;
}
